/**
 * PlanSummaryCard
 * Responsabilidade: Exibir o resumo do plano ativo com gradiente e informações de licenças.
 * Princípios: Single Responsibility, tokens semânticos do design system.
 */
import React from 'react';
import { Button } from '../../../components/ui/Button';
import { formatCurrency } from '../../../utils/formatters';
import { TrialBadge } from './TrialBadge';

export interface PlanSummaryCardProps {
    customerName: string;
    isTrialing: boolean;
    planName: string;
    quantity: number;
    pricePerUnit: number;
    totalPrice: number;
    hasActiveSubscription: boolean;
    isPortalLoading: boolean;
    onManageSubscription: () => void;
    onManageLicenses: () => void;
    usedLicenses: number;
    isMembersLoading?: boolean;
    trialDaysLeft?: number;
    className?: string;
}

export const PlanSummaryCard: React.FC<PlanSummaryCardProps> = ({
    customerName,
    isTrialing,
    planName,
    quantity,
    pricePerUnit,
    totalPrice,
    hasActiveSubscription,
    isPortalLoading,
    onManageSubscription,
    onManageLicenses,
    usedLicenses,
    isMembersLoading = false,
    trialDaysLeft = 7,
    className,
}) => {
    const displayUsed = isMembersLoading ? '—' : usedLicenses;
    const displayAvailable = isMembersLoading ? '—' : Math.max(0, quantity - usedLicenses);
    const displayTotal = isMembersLoading ? quantity : Math.max(quantity, usedLicenses);

    return (
        <article
            className={`rounded-2xl overflow-hidden bg-gradient-conversion text-white shadow-brand flex flex-col ${className || ''}`}
            aria-label="Resumo da assinatura ativa"
        >
            {/* Cabeçalho do card */}
            <header className="px-6 pt-6 pb-5 border-b border-white/20">
                <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest mb-1.5">
                    {isTrialing && !hasActiveSubscription ? 'Período de teste' : 'Assinatura Ativa'}
                </p>
                <p className="font-bold text-xl text-white leading-tight">{customerName}</p>
                <div className="mt-3 flex items-center gap-2 flex-wrap">
                    {isTrialing && <TrialBadge daysLeft={trialDaysLeft} size="md" />}
                    <span className="px-2.5 py-1 text-[10px] font-bold text-white uppercase tracking-widest border border-white/30 rounded-lg bg-white/10">
                        Faturado Mensalmente
                    </span>
                </div>
            </header>

            {/* Corpo do card */}
            <div className="px-6 py-6 flex-1 flex flex-col justify-center">
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6">

                    {/* Coluna esquerda — plano e licenças */}
                    <div className="flex flex-col gap-2 min-w-0">
                        <p className="text-[10px] font-bold text-white/70 uppercase tracking-widest">
                            Seu Plano
                        </p>
                        <p className="text-3xl font-bold text-white capitalize leading-none">
                            {planName}
                        </p>

                        {/* Detalhes de licenças */}
                        <div className="mt-1 flex flex-wrap items-center gap-x-1 gap-y-1 text-sm text-white/80">
                            <span>{displayTotal} {displayTotal === 1 ? 'usuário' : 'usuários'}</span>
                            <span className="text-white/50">
                                ({displayUsed} em uso, {displayAvailable} disponível)
                            </span>
                            <span className="text-white/50">×</span>
                            {isTrialing ? (
                                <>
                                    <span className="line-through text-white/50">{formatCurrency(pricePerUnit)}</span>
                                    <span className="font-medium text-white">{formatCurrency(0)}</span>
                                </>
                            ) : (
                                <span className="text-white">{formatCurrency(pricePerUnit)}</span>
                            )}
                            <span className="text-white/50">/ por usuário</span>
                            <button
                                onClick={onManageLicenses}
                                className="text-xs font-semibold text-white underline underline-offset-2 hover:text-white/80 transition-colors"
                            >
                                Gerenciar usuários
                            </button>
                        </div>
                    </div>

                    {/* Coluna direita — botão + preço total */}
                    <div className="flex flex-col items-start sm:items-end gap-3 shrink-0 mt-auto sm:mt-0">
                        <Button
                            variant="outline"
                            onClick={onManageSubscription}
                            disabled={isPortalLoading}
                            className="bg-white/10 border-white/25 text-white hover:bg-white/20 hover:text-white whitespace-nowrap"
                        >
                            {isPortalLoading
                                ? 'Abrindo...'
                                : isTrialing && !hasActiveSubscription
                                    ? 'Assinar plano'
                                    : 'Gerencie sua assinatura'}
                        </Button>

                        <div className="text-right">
                            {isTrialing ? (
                                <p className="flex items-baseline gap-2">
                                    <span className="line-through text-white/50 text-base font-normal">
                                        {formatCurrency(totalPrice)}
                                    </span>
                                    <span className="text-3xl font-bold text-white leading-none">
                                        {formatCurrency(0)}
                                    </span>
                                </p>
                            ) : (
                                <p className="flex items-baseline gap-1">
                                    <span className="text-3xl font-bold text-white leading-none">
                                        {formatCurrency(totalPrice)}
                                    </span>
                                    <span className="text-sm font-normal text-white/70">/ mês</span>
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
};

export default PlanSummaryCard;
